/**
 * Utility functions for JWT token validation
 */

interface DecodedToken {
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/**
 * Decode JWT token without verification (client-side only)
 * Note: This doesn't verify the signature, just extracts the payload
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as DecodedToken;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Check if token is expired or will expire soon
 * @param token - JWT token string
 * @param bufferSeconds - Buffer time in seconds before expiration to consider token as expired (default: 300 = 5 minutes)
 * @returns true if token is expired or will expire soon
 */
export function isTokenExpired(
  token: string | null,
  bufferSeconds = 300
): boolean {
  if (!token) {
    return true;
  }

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    // If we can't decode or no expiration, consider it expired to be safe
    return true;
  }

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = bufferSeconds * 1000;

  // Token is expired if current time + buffer >= expiration time
  return currentTime + bufferTime >= expirationTime;
}

/**
 * Get token expiration time in milliseconds
 */
export function getTokenExpiration(token: string | null): number | null {
  if (!token) {
    return null;
  }

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  return decoded.exp * 1000; // Convert to milliseconds
}

